import {
  CARBINE,
  SPECIES,
  spawnProjectile,
  stepProjectile,
  type CollisionWorld,
  type EntityTarget,
  type MapBox,
  type ProjectileState,
  type Vec3,
  type WeaponStats,
} from "@rietz/shared";
import type { InterpPose } from "../net/interpolation.js";
import type { GameAudio } from "../audio/synth.js";
import type { Fx } from "./fx.js";
import { pulseHitMarker } from "../ui/store.js";

/**
 * Rounds in the air, as the client draws them. Every shot — the player's and
 * everybody else's — is flown with the SAME `stepProjectile` the server steps,
 * so travel time, drop and penetration are not re-derived. Each live bullet
 * drives one bolt in `Fx`'s instanced pool, placed at the simulated position
 * and nosed along the simulated VELOCITY every frame.
 *
 * Nothing here is authoritative: no health, no `hit`, nobody else sees it; it
 * pulses the faintest hit marker when the player's own round visually reaches
 * a pose. It differs from the server in that poses are the INTERPOLATED ones
 * (no rewind), the step is a rendered frame, and the shooter's flight starts
 * from the predicted eye. Fixed pool, recycled oldest-first, as in `Fx`.
 */

/** Rounds that may be in flight at once. Eight players at 400 rpm never come
 *  close — a carbine round crosses the whole 600 m range in under a second. */
const MAX_BULLETS = 24;

/** One round in flight, plus the `Fx` bolt slot it is driving. */
interface Bullet {
  /** `null` means the slot is free. */
  state: ProjectileState | null;
  /** Ours, so a body hit is worth a predicted marker. */
  local: boolean;
  /** `Fx` bullet handle, or `-1` when the pool had none to give. */
  slot: number;
  /** Birth stamp, for oldest-first recycling. */
  born: number;
  /**
   * Whether this round has already snapped past the player's head. ONE whiz per
   * round — a slow round can spend two frames inside the radius.
   */
  whizzed: boolean;
}

export interface VisualBulletsOptions {
  fx: Fx;
  /**
   * Impacts and near misses. Rounds are the one thing that makes a noise where
   * the player is not, and this pool is the only code that knows where they
   * are.
   */
  audio: GameAudio;
  /** Getters — the collision world and solids are swapped on every map change. */
  world: () => CollisionWorld;
  /** Hard solids indexed by non-terrain RayHit.boxIndex values. */
  solids: () => readonly MapBox[];
  /** The poses currently being drawn for remote entities. */
  poses: () => readonly InterpPose[];
  /**
   * A round that ended in geometry, and where. The hideout range uses it to
   * match the point against its plates — that test belongs to the range. Called
   * AFTER the effect and audio; `local` for the player's own round; `pos` is
   * scratch, do not keep it.
   */
  onImpact?: (pos: Readonly<Vec3>, local: boolean) => void;
}

export class VisualBullets {
  private readonly o: VisualBulletsOptions;

  private readonly bullets: Bullet[] = [];
  /** Live count, so an idle frame costs one comparison instead of a scan. */
  private live = 0;

  /** Monotonic counter used only to order recycling. */
  private stamp = 0;

  /**
   * Target list for `stepProjectile`, plus the pool backing it. Built ONCE per
   * update (the shooter exclusion is `stepProjectile`'s own); `pool` grows to
   * the high-water mark and is reused, `view` is truncated and refilled.
   */
  private readonly pool: EntityTarget[] = [];
  private readonly view: EntityTarget[] = [];

  /** Unit flight direction for the bolt being placed this frame. Scratch: the
   *  orientation is recomputed per round per frame and must not allocate. */
  private readonly dir: Vec3 = { x: 0, y: 0, z: 0 };

  /** Where the round being stepped started this frame, for the whiz test.
   *  Scratch, for the same no-allocation reason as `dir`. */
  private readonly from: Vec3 = { x: 0, y: 0, z: 0 };

  constructor(opts: VisualBulletsOptions) {
    this.o = opts;
    for (let i = 0; i < MAX_BULLETS; i++) {
      this.bullets.push({ state: null, local: false, slot: -1, born: 0, whizzed: false });
    }
  }

  /**
   * Puts a round in the air. `shooterId` is only for self-exclusion, so the
   * local player passes `""`. `stats` decides the trajectory: the player's
   * EFFECTIVE weapon, or for somebody else the weapon def's BASE stats from the
   * `shot` event's `weaponId` — a bystander is never told what is loaded or
   * bolted on. Default is the carbine.
   */
  spawn(
    origin: Vec3,
    dir: Vec3,
    shooterId: string,
    local: boolean,
    stats: WeaponStats = CARBINE,
  ): void {
    const b = this.claim();
    b.state = spawnProjectile(stats, shooterId, origin, dir);
    b.local = local;
    b.born = ++this.stamp;
    b.whizzed = false;
    b.slot = this.o.fx.bulletClaim();
    // Placed at the muzzle already pointing where the shot was aimed, so the
    // round is oriented on the frame of the shot.
    this.o.fx.bulletSet(b.slot, origin, dir);
    this.live += 1;
  }

  /**
   * Flies every round by one rendered frame. `dtMs` is clamped as `Fx.update`
   * clamps it — a stalled frame must not teleport every round past what it was
   * about to hit.
   */
  update(dtMs: number, nowMs: number): void {
    if (this.live === 0) return;
    const dt = Math.min(Math.max(dtMs, 0), 100) / 1000;
    if (dt <= 0) return;

    const world = this.o.world();
    const solids = this.o.solids();
    const targets = this.buildTargets();
    const dir = this.dir;
    const from = this.from;
    const audio = this.o.audio;

    for (const b of this.bullets) {
      const state = b.state;
      if (state === null) continue;

      // Copied BEFORE the step, because `stepProjectile` is handed the state it
      // is about to replace and the whiz below needs the segment, not a point.
      from.x = state.pos.x;
      from.y = state.pos.y;
      from.z = state.pos.z;

      // The one allocation in the steady-state frame path: `stepProjectile`
      // returns a fresh state. Keeping the shared sim pure is worth it.
      const res = stepProjectile(state, world, solids, targets, dt);

      // Near misses BEFORE the outcome: a round that ends this frame may still
      // have gone past the player's head. LOCAL rounds are excluded — the
      // player's own bullet leaves from between their eyes.
      if (!b.local && !b.whizzed) {
        const end = res.state?.pos ?? res.wallStop ?? res.hit?.pos ?? null;
        if (end !== null && audio.whiz(from, end)) b.whizzed = true;
      }

      if (res.hit !== null) {
        // Nothing final is drawn: retiring hides the bolt, which is right — the
        // round is inside somebody, and the marker below is the feedback.
        this.retire(b);
        // Client-side against interpolated poses, so occasionally wrong — hence
        // the faintest marker.
        if (b.local) pulseHitMarker("predicted", nowMs);
        continue;
      }

      if (res.wallStop !== null) {
        // The bolt vanishes and the puff takes over as the landing cue.
        this.retire(b);
        this.o.fx.impact(res.wallStop);
        // `wallMaterial` is non-null exactly when `wallStop` is; the fallback
        // keeps
        // an unlabelled hit from sounding like silence.
        audio.impact(res.wallStop, res.wallMaterial ?? "concrete");
        // Last, so the range's own feedback lands on top of the generic one.
        this.o.onImpact?.(res.wallStop, b.local);
        continue;
      }

      // A wall the round went THROUGH produces no puff: `stepProjectile`
      // traverses
      // it inside the step and reports nothing.

      if (res.state === null) {
        // Past `MAX_PROJECTILE_RANGE`, or a degenerate direction: the round is
        // simply gone.
        this.retire(b);
        continue;
      }

      b.state = res.state;

      // VELOCITY, not the frame's displacement, is the facing: it carries the
      // drop, so the bolt noses over as it falls.
      const v = res.state.vel;
      const len = Math.hypot(v.x, v.y, v.z);
      // A live state cannot have zero velocity; the guard keeps a future change
      // from producing a NaN quaternion.
      if (len > 0) {
        dir.x = v.x / len;
        dir.y = v.y / len;
        dir.z = v.z / len;
        this.o.fx.bulletSet(b.slot, res.state.pos, dir);
      }
    }
  }

  /**
   * Drops every round in flight — on a map swap, a round in the air was fired
   * at geometry that no longer exists.
   */
  clear(): void {
    for (const b of this.bullets) {
      if (b.state !== null) this.retire(b);
    }
  }

  // -----------------------------------------------------------------------

  /**
   * A free slot, else the oldest live one — its `Fx` bolt handed back first, so
   * no bullet hangs with nobody driving it.
   */
  private claim(): Bullet {
    let oldest = this.bullets[0] as Bullet;
    let oldestBorn = Infinity;
    for (const b of this.bullets) {
      if (b.state === null) return b;
      if (b.born < oldestBorn) {
        oldestBorn = b.born;
        oldest = b;
      }
    }
    this.retire(oldest);
    return oldest;
  }

  /** Frees a slot; releasing the `Fx` handle takes the bolt off screen. */
  private retire(b: Bullet): void {
    this.o.fx.bulletRelease(b.slot);
    b.slot = -1;
    b.state = null;
    b.whizzed = false;
    this.live -= 1;
  }

  /**
   * The shootable entities for this frame. Poses are taken BY REFERENCE (an
   * `InterpPose` is a `HitPose`). A corpse is skipped — a round stopping in one
   * swallows the bolt for no usable feedback. An ANIMAL brings its own boxes,
   * or a hare would be flown at as a 1.8 m man; `undefined` takes
   * `raycastPose`'s shared per-stance arrays.
   */
  private buildTargets(): readonly EntityTarget[] {
    this.view.length = 0;

    let slot = 0;
    for (const pose of this.o.poses()) {
      if (pose.anim === "dead") continue;
      const target = this.targetAt(slot);
      target.id = pose.id;
      target.pose = pose;
      target.boxes =
        pose.species === undefined ? undefined : SPECIES[pose.species].boxes;
      this.view.push(target);
      slot++;
    }

    return this.view;
  }

  /** Pooled `EntityTarget` for slot `i`, created on first use. */
  private targetAt(i: number): EntityTarget {
    let target = this.pool[i];
    if (target === undefined) {
      // Both fields are overwritten before the target is ever read; the pose
      // here exists only so the slot is a complete `EntityTarget` from birth.
      target = { id: "", pose: { pos: { x: 0, y: 0, z: 0 }, yaw: 0, stance: "stand" } };
      this.pool[i] = target;
    }
    return target;
  }
}
